'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { ChevronDown, Star, StarOff } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@kit/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@kit/ui/shadcn-sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { cn, isRouteActive } from '@kit/ui/utils';

import { BrandLink } from '~/components/brand-link';
import { useNavigationBadgeCounts } from '../_lib/hooks/use-navigation-badges';
import { adminNavigationGroups } from './admin-tab-context';
import { useFavoritesOptional } from './sidebar-favorites';

// Admin menu = single source of truth in admin-tab-context.tsx (Rule #8)
const adminGroups = adminNavigationGroups;

// Check if any item in a group is active
function hasActiveChild(
  items: { path: string; end?: boolean }[],
  pathname: string,
): boolean {
  return items.some((item) => isRouteActive(item.path, pathname, item.end));
}

interface SuperAdminTabbedNavigationProps {
  userId: string;
  dancerProfileId?: string | null;
  clientId?: string | null;
}

export function SuperAdminTabbedNavigation({
  userId,
  dancerProfileId,
  clientId,
}: SuperAdminTabbedNavigationProps) {
  const pathname = usePathname();
  const favoritesContext = useFavoritesOptional();

  // Manual open/close overrides, keyed by group label. Without an override a
  // group is open iff it holds the active route, so the accordion follows
  // navigation; a user's explicit toggle wins until they navigate the
  // pathname into/out of that group again (override resets on pathname
  // change below).
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>(
    {},
  );
  // Reset manual overrides on navigation — the documented
  // adjust-state-during-render pattern (react.dev "You Might Not Need an
  // Effect"), so the accordion re-follows the active route.
  const [trackedPathname, setTrackedPathname] = useState(pathname);
  if (trackedPathname !== pathname) {
    setTrackedPathname(pathname);
    setOpenOverrides({});
  }

  const { counts } = useNavigationBadgeCounts({
    userId,
    dancerProfileId: dancerProfileId ?? null,
    isSuperAdmin: true,
    clientId: clientId ?? null,
  });

  // Badge counts by path
  const getBadgeCount = (path: string): number => {
    switch (path) {
      case '/admin/hire-orders':
        return counts.adminPendingHireOrders;
      case '/admin/finance/invoices':
        return counts.adminPendingInvoices;
      case '/admin/finance/reimbursements':
        return counts.adminPendingReimbursements;
      default:
        return 0;
    }
  };

  return (
    <>
      {adminGroups.map((group) => {
        // Accordion behavior (Zoho/Dropbox-admin pattern, 2026-07-05
        // wall-of-items feedback): only the group holding the active route
        // opens; the rest collapse to their headers so the menu reads as
        // ~6 rows, not ~30.
        const open =
          openOverrides[group.label] ?? hasActiveChild(group.items, pathname);

        return (
          <Collapsible
            key={group.label}
            open={open}
            onOpenChange={(next) =>
              setOpenOverrides((prev) => ({ ...prev, [group.label]: next }))
            }
            className="group/collapsible"
          >
            <SidebarGroup className="py-0">
              <SidebarGroupLabel asChild className="py-0">
                <CollapsibleTrigger className="flex w-full items-center justify-between">
                  <span>{group.label}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 ease-in-out group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => {
                      const isActive = isRouteActive(
                        item.path,
                        pathname,
                        item.end,
                      );
                      const badgeCount = getBadgeCount(item.path);
                      const isFavorite = favoritesContext?.isFavorite(
                        item.path,
                      );
                      const canAddMore = favoritesContext?.canAddMore ?? true;

                      return (
                        <SidebarMenuItem
                          key={item.path}
                          className="group/nav-item"
                        >
                          <SidebarMenuButton asChild isActive={isActive}>
                            <BrandLink
                              to={item.path}
                              className="flex items-center"
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.label}</span>
                            </BrandLink>
                          </SidebarMenuButton>

                          {/* Pin button - shows on hover */}
                          {favoritesContext && (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                      'absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 opacity-0 transition-opacity group-hover/nav-item:opacity-100',
                                      isFavorite &&
                                        'text-yellow-500 opacity-100',
                                    )}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      favoritesContext.toggleFavorite(
                                        item.path,
                                      );
                                    }}
                                    disabled={!isFavorite && !canAddMore}
                                  >
                                    {isFavorite ? (
                                      <StarOff className="h-3.5 w-3.5" />
                                    ) : (
                                      <Star className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  {isFavorite
                                    ? 'Remove from favorites'
                                    : canAddMore
                                      ? 'Add to favorites'
                                      : 'Maximum 5 favorites reached'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {badgeCount > 0 && (
                            <SidebarMenuBadge
                              className={cn(
                                'bg-destructive text-white',
                                // Shift badge left when favorite button exists to avoid overlap
                                favoritesContext && 'right-8',
                              )}
                            >
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </SidebarMenuBadge>
                          )}
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        );
      })}
    </>
  );
}
